import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import moment from "moment";
import { StatCard } from "@outpave/ds";
import { formatCashFull } from "utils/formatCash";
import { ComplianceMetrics } from "hooks/useByocDashboard";

export interface ReceiptsMissingCardProps {
  metrics: ComplianceMetrics;
  loading?: boolean;
  /** The query failed with nothing cached, so `metrics` is the empty fallback
   *  rather than a measurement. Without this the card reads a failure as a clean
   *  bill of health. */
  error?: boolean;
}

/**
 * Size of the missing-receipt backlog: how many, how much, how old, and who is
 * holding up the most of it. Opens the Card Expenses tasks view, which applies
 * the same compliance filter.
 */
const ReceiptsMissingCard: React.FC<ReceiptsMissingCardProps> = ({
  metrics,
  loading = false,
  error = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const currency = metrics.currency ?? "usd";
  const hasBacklog = metrics.count > 0;

  // Whole days, floored: "oldest 45 days" should not tick over until the day
  // actually completes.
  const oldestDays = metrics.oldestTransactionDate
    ? moment().diff(moment(metrics.oldestTransactionDate), "days")
    : null;

  // The resolver returns byUser sorted by count descending, so the worst
  // offender is simply the first entry.
  const topUser = metrics.byUser[0];
  const topUserName = topUser
    ? [topUser.firstName, topUser.lastName].filter(Boolean).join(" ")
    : "";

  // Amount leads in the warning colour, the rest stays tertiary. StatCard keeps
  // `caption` to a single line and ellipsises the overflow, so the most
  // load-bearing fact goes first.
  const detail = [
    oldestDays !== null && t("oldest {{n}} days", { n: oldestDays }),
    topUserName &&
      topUser &&
      t("{{name}} owes {{n}} receipts", {
        name: topUserName,
        n: topUser.count,
      }),
  ].filter(Boolean);

  return (
    <StatCard
      title={t("Receipts Missing")}
      value={error ? "-" : metrics.count}
      loading={loading}
      caption={
        error ? (
          t("Something went wrong")
        ) : hasBacklog ? (
          <>
            <Box
              component="span"
              sx={{ color: "var(--color-text-danger)", marginRight: "6px" }}
            >
              {formatCashFull(metrics.totalAmount, currency, false)}
            </Box>
            {detail.join(" · ")}
          </>
        ) : (
          t("Every transaction has a receipt")
        )
      }
      action={{ type: "open", onClick: () => navigate("/admin/transactions") }}
    />
  );
};

export default ReceiptsMissingCard;
